// API client.
//
// Every call goes to the same origin and relies on the httpOnly session cookie, so there are
// no tokens to store and nothing for a script on the page to steal.

export class ApiError extends Error {
  status: number;
  /** Section names the server says are still outstanding, on a 409 from submit. */
  missing?: string[];

  constructor(status: number, message: string, missing?: string[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (missing) this.missing = missing;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    // Belt and braces: the cookie is same-origin, but be explicit about sending it.
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof data.error === 'string' ? data.error : 'Something went wrong. Please try again.',
      Array.isArray(data.missing) ? (data.missing as string[]) : undefined,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// --- shapes the server returns ---------------------------------------------

export interface CycleInfo {
  year: number;
  acceptingApplications: boolean;
  reason: 'open' | 'not-yet-open' | 'closed' | 'disabled';
  opensAt: string;
  closesAt: string;
  /** In whole dollars. Per cycle: the award was $10,000 for years and is $20,000 for 2027. */
  awardAmount: number;
  awardYears: number;
}

export interface ApplicantSummary {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  completed: boolean;
  cycleYear: number;
  mustChangePassword: boolean;
}

export interface SessionResponse {
  applicant: ApplicantSummary | null;
  cycle: CycleInfo | null;
}

export interface SectionStatus {
  key: string;
  label: string;
  done: boolean;
  required: boolean;
}

export interface ApplicationResponse {
  applicant: {
    id: number;
    email: string;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    telephone: string | null;
    dob: string | null;
    gender: string | null;
    completed: boolean;
    submittedAt: string | null;
    verificationForm: boolean;
    cycleYear: number;
    emailOptIn: boolean;
    unsubscribed: boolean;
  };
  status: { sections: SectionStatus[]; complete: boolean; submitted: boolean };
  cycle: { year: number; closesAt: string } | null;
}

export type SectionRow = Record<string, string | number | boolean | null> & { id: number };

export interface EssayQuestion {
  id: number;
  question: string;
  max_length: number;
  position: number;
  answer: string | null;
}

export interface FinancialOption { id: number; income?: string; siblings?: string }

export interface FinancialResponse {
  options: { income: Array<{ id: number; income: string }>; siblings: Array<{ id: number; siblings: string }> };
  current: { income_option_id: number | null; sibling_option_id: number | null } | null;
}

export interface AdminSummary {
  id: number;
  email: string;
  name: string | null;
  role: 'reviewer' | 'admin';
  canConfigure: boolean;
}

export interface AdminCycle {
  id: number;
  year: number;
  opensAt: string;
  closesAt: string;
  isOpen: boolean;
  isCurrent: boolean;
  awardAmount: number;
  awardYears: number;
  total: number;
  completed: number;
}

export interface ScoredApplicant {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  telephone: string | null;
  city: string | null;
  state: string | null;
  submittedAt: string | null;
  income: string | null;
  siblings: string | null;
  gpa: string | null;
  sat: number | null;
  satScale: number | null;
  act: number | null;
  incomePoints: number;
  siblingPoints: number;
  testPoints: number | null;
  gpaPoints: number | null;
  totalPoints: number;
  awards: number;
  extracurriculars: number;
  communityActivities: number;
  jobs: number;
  recommendations: number;
}
